import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RemoveIcon from '@mui/icons-material/Remove';
import SyncIcon from '@mui/icons-material/Sync';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { MappedModuleWithChildren } from 'interfaces/Interfaces';
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from 'react-router-dom';
import { buildAdoPrUrl, stripHtml } from 'utils/utils';
import RequestStatusChip from './RequestStatusChip';
import ResourceContextMenu from './ResourceContextMenu';
import ReviewStatusChip from './ReviewStatusChip';

const SUB_PAGE_SIZE = 5;

type ExpandableUserResourceRowProps = {
  request: MappedModuleWithChildren;
  level?: number;
  resourceFilter?: string | null;
  platformFilter?: string | null;
  showSucceededResourcesFilter?: boolean;
  showDeletedResourcesFilter?: boolean;
  onDeleteSuccess: () => void;
  onLoadParentDetails?: (request: MappedModuleWithChildren) => void;
  loadingDetailsForId?: string | null;
  failedDetailsIds?: Set<string>;
  isDefaultExpanded: boolean;
}

const normalizeText = (value: unknown): string => {
  if (value == null) {
    return "";
  }

  return stripHtml(String(value)).toLowerCase().trim();
};

const compactText = (value: string): string => value.replace(/[^a-z0-9]+/g, '');

function rowMatchesFilter(request: MappedModuleWithChildren, resourceFilter: any, platformFilter: any) {
  const resourceFilterValue = normalizeText(resourceFilter);
  const platformFilterValue = normalizeText(platformFilter);
  const prefixedResourceName = `${request.parentName?.prefix || ''}${request.resourceName || ''}${request.parentName?.suffix || ''}`;

  const requestText = normalizeText([
    request.platform || '',
    request.title || '',
    request.displayResourceName || '',
    (request as any).displayName || '',
    request.resourceName || '',
    request.resolvedResourceName || '',
    prefixedResourceName,
    request.parent?.resource || ''
  ].join(' '));

  const matchesResource =
    !resourceFilterValue ||
    requestText.includes(resourceFilterValue) ||
    compactText(requestText).includes(compactText(resourceFilterValue));

  const matchesPlatform = !platformFilterValue || normalizeText(request.platform).includes(platformFilterValue);

  return matchesResource && matchesPlatform;
}

function getRequestDisplayLabel(request: MappedModuleWithChildren): string {
  const baseTitle = stripHtml(request.title || '').trim();
  const displayResourceName = request.displayResourceName == null
    ? ''
    : String(request.displayResourceName).trim();

  if (!displayResourceName) {
    return baseTitle;
  }

  const titleWithoutResourceSuffix = baseTitle.replace(/\s*\([^()]*\)\s*$/, '').trim();
  return titleWithoutResourceSuffix
    ? `${titleWithoutResourceSuffix} (${displayResourceName})`
    : displayResourceName;
}

function ExpandableUserResourceRow({ request, level = 0, resourceFilter, showSucceededResourcesFilter, showDeletedResourcesFilter, onDeleteSuccess, onLoadParentDetails, loadingDetailsForId, failedDetailsIds, isDefaultExpanded, platformFilter }: ExpandableUserResourceRowProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(isDefaultExpanded || false);
  const [menuOrDialogOpen, setMenuOrDialogOpen] = useState(false);
  const [subPage, setSubPage] = useState(1);
  const [visibleChildren, setVisibleChildren] = useState<MappedModuleWithChildren[]>([]);
  const requestDisplayLabel = getRequestDisplayLabel(request);

  const shouldIncludeChildByStatus = useCallback((child: MappedModuleWithChildren): boolean => {
    const includeCancelled = showSucceededResourcesFilter ? child.status !== "cancelled" : true;
    const isCompletedDelete = child.statusType === "delete" && child.prStatus === "completed";
    const includeDeleted = showDeletedResourcesFilter ? !isCompletedDelete : true;
    return includeCancelled && includeDeleted;
  }, [showSucceededResourcesFilter, showDeletedResourcesFilter]);

  let filteredChildren: MappedModuleWithChildren[];
  if (rowMatchesFilter(request, resourceFilter, platformFilter)) {
    filteredChildren = request.children
      .filter(child => shouldIncludeChildByStatus(child));
  } else {
    filteredChildren = request.children
      .filter(child => shouldIncludeChildByStatus(child))
      .filter(childRequest => rowMatchesFilter(childRequest, resourceFilter, platformFilter));
  }

  useEffect(() => {
    setIsExpanded(isDefaultExpanded);
  }, [isDefaultExpanded]);

  useEffect(() => {
    if (isExpanded) {
      let filteredChildren: MappedModuleWithChildren[];

      if (rowMatchesFilter(request, resourceFilter, platformFilter)) {
        // Parent matched: alle Sub-Rows anzeigen (nur ggf. nach Status filtern)
        filteredChildren = request.children
          .filter(child => shouldIncludeChildByStatus(child));
      } else {
        // Parent matched nicht: Sub-Rows nach Filter filtern
        filteredChildren = request.children
          .filter(child => shouldIncludeChildByStatus(child))
          .filter(childRequest => rowMatchesFilter(childRequest, resourceFilter, platformFilter));
      }

      setVisibleChildren(filteredChildren.slice(0, subPage * SUB_PAGE_SIZE));
    } else {
      setVisibleChildren([]);
    }
  }, [isExpanded, subPage, request, request.children, resourceFilter, platformFilter, showSucceededResourcesFilter, showDeletedResourcesFilter, shouldIncludeChildByStatus]);

  useEffect(() => {
    const hasTextFilter = normalizeText(resourceFilter).length > 0;
    if (!hasTextFilter) {
      return;
    }

    if (filteredChildren.length > 0) {
      setIsExpanded(true);
    }
  }, [resourceFilter, filteredChildren.length]);

  useEffect(() => {
    setSubPage(1);
  }, [isExpanded, resourceFilter, platformFilter, showSucceededResourcesFilter, showDeletedResourcesFilter, request.children]);

  return (
    <>

      <TableRow
        hover
        key={request.id}
        sx={{
          '&:last-child td, &:last-child th':
          {
            border: 0
          },
          '& .MuiTableCell-sizeMedium': {
            paddingTop: level > 0 ? '3px' : '10px',
            paddingBottom: level > 0 ? '3px' : '10px'
          }
        }}
      >
        <TableCell padding='none' sx={{ paddingLeft: '5px' }}>
          {request.children.length !== 0 ?
            <IconButton onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            :
            level === 0 &&
            <IconButton disabled>
              <RemoveIcon />
            </IconButton>
          }
        </TableCell>
        <TableCell component="th" scope="row">
          {request.pullRequestId && !(request.statusType === 'delete' && request.prStatus === 'completed') ?
            <Link component={RouterLink} to={`/user/requests/${request.latestPullRequestId || request.pullRequestId}`} color="inherit" underline="hover">
              <p style={{ display: 'flex', alignItems: 'center', paddingLeft: level > 0 ? '10px' : 0 }}>
                <img
                  alt=""
                  src={`/icons/${request.platform}-${request.name}.svg`}
                  style={{ maxWidth: 18, maxHeight: 18 }}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = "/icons/default.svg";
                  }} />
                <span style={{ fontWeight: level > 0 ? 'normal' : 'bold', paddingLeft: '10px' }}>
                  <span style={{ textTransform: 'capitalize' }}>{request.platform}</span>&nbsp;{requestDisplayLabel}
                </span>
              </p>
            </Link>
            :
            <p style={{ display: 'flex', alignItems: 'center', paddingLeft: level > 0 ? '10px' : 0 }}>
              <img
                alt=""
                src={`/icons/${request.platform}-${request.name}.svg`}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null; // prevents looping
                  currentTarget.src = "/icons/default.svg";
                }} />
              <span style={{ fontWeight: level > 0 ? 'normal' : 'bold', paddingLeft: '10px' }}>
                <span style={{ textTransform: 'capitalize' }}>{request.platform}</span>&nbsp;{requestDisplayLabel}
              </span>
            </p>
          }
        </TableCell>
        <TableCell>
          {request.status &&
            (<ReviewStatusChip reviewers={request.checks.review.reviewers} />)
          }
        </TableCell>
        <TableCell>
          <div style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}>
            {request.requester}
          </div>
        </TableCell>
        <TableCell>
          {request.status &&
            (<Tooltip title={request.updatedDate.toLocaleString()} placement="left" arrow>
              <span>{request.updatedDate.toLocaleDateString()}</span>
            </Tooltip>)
          }
        </TableCell>
        <TableCell>
          <RequestStatusChip 
            status={request.status} 
            type={request.statusType} 
            description={request.statusDescription}
            onClick={!request.pullRequestId
              ? (!failedDetailsIds?.has(request.id) && onLoadParentDetails ? () => onLoadParentDetails(request) : undefined)
              : (() => {
                const prUrl = buildAdoPrUrl(request.repository?.url, request.latestPullRequestId || request.pullRequestId);
                return prUrl ? () => window.open(prUrl, '_blank', 'noopener,noreferrer') : undefined;
              })()}
            loading={loadingDetailsForId === request.id}
            trailingIcon={
              request.hasAccessChecks ? (
                <Tooltip title="Access control enabled" placement="bottom" arrow>
                  <Box sx={{ 
                    width: 22, 
                    height: 22, 
                    borderRadius: '50%', 
                    bgcolor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <LockOpenIcon sx={{ fontSize: 16, color: 'success.main', ml: '1px', mt: '-1px' }} />
                  </Box>
                </Tooltip>
              ) : undefined
            }
          />
        </TableCell>
        <TableCell
          sx={{
            '& .menu-container': {
              opacity: (menuOrDialogOpen) ? 1 : 0,
              pointerEvents: (menuOrDialogOpen) ? 'auto' : 'none',
              transition: 'opacity 0.2s'
            }
          }}
        >
          <div className="menu-container">
            {(request.status) && <ResourceContextMenu
              request={request}
              onDeleteSuccess={onDeleteSuccess}
              onMenuOpen={() => setMenuOrDialogOpen(true)}
              onMenuClose={() => setMenuOrDialogOpen(false)}
            />}
          </div>
        </TableCell>
      </TableRow >

      {
        isExpanded && (
          visibleChildren.map(childRequest => (
            <ExpandableUserResourceRow
              key={childRequest.pullRequestId}
              request={childRequest}
              level={level + 1}
              resourceFilter={resourceFilter}
              platformFilter={platformFilter}
              showSucceededResourcesFilter={showSucceededResourcesFilter}
              showDeletedResourcesFilter={showDeletedResourcesFilter}
              onDeleteSuccess={onDeleteSuccess}
              onLoadParentDetails={onLoadParentDetails}
              loadingDetailsForId={loadingDetailsForId}
              failedDetailsIds={failedDetailsIds}
              isDefaultExpanded={false}
            />
          ))
        )
      }
      {
        isExpanded &&
        visibleChildren.length < filteredChildren.length && (
          <TableRow>
            <TableCell colSpan={7} align="center">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setSubPage(subPage + 1)}
                sx={{
                  mt: 1,
                  mb: 1,
                  pl: 2,
                  pr: 2,
                  borderRadius: 5,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: 'none'
                }}
                size="small"
                startIcon={<SyncIcon />}
              >
                Load more ...
              </Button>
            </TableCell>
          </TableRow>
        )
      }
    </>
  );
};

export default ExpandableUserResourceRow;
